import { Form } from "@unform/web"
import { Building, DollarSign, TrendingDown, TrendingUp } from "lucide-react"
import React, { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback } from "react"
import { useSelector } from "react-redux"
import Input from "~/components/RegisterForm/Input"
import RegisterFormMenu from "~/components/RegisterForm/Menu"
import InputLine from "~/components/RegisterForm/InputLine"
import InputLineGroup from "~/components/RegisterForm/InputLineGroup"
import FormHeader from "~/components/RegisterForm/FormHeader"
import Preview from "~/components/Preview"
import { Zoom, toast } from "react-toastify"
import FormLoading from "~/components/RegisterForm/FormLoading"
import { FullGridContext } from "../../../"
import Textarea from "~/components/RegisterForm/Textarea"
import AccountsMultiSelect from "./AccountsMultiSelect"
import api from "~/services/api"
import FindGeneric from "~/components/Finds/FindGeneric"

export const InputContext = createContext({})

const dateTypeOptions = [
  { value: "accrual_date", label: "Accrual Date" },
  { value: "settlement_date", label: "Settlement Date" },
  { value: "due_date", label: "Due Date" },
]

export default function PagePreview({ access, id, defaultFormType = "full" }) {
  const { handleOpened, setSuccessfullyUpdated } = useContext(FullGridContext)
  const auth = useSelector((state) => state.auth)
  const [pageData, setPageData] = useState({
    loaded: false,
    cashflow_name: "",
    cashflow_code: "",
    description: "",
    date_type: "due_date",
    operating_revenues_accounts: [],
    operating_expenses_accounts: [],
    investing_revenues_accounts: [],
    capex_accounts: [],
    financing_inflows_accounts: [],
    financing_outflows_accounts: [],
    filial_id: null,
    filial: null,
  })

  const [selectedDateType, setSelectedDateType] = useState("due_date")
  const [activeMenu, setActiveMenu] = useState("general")
  const [loading, setLoading] = useState(false)
  const [formInitialized, setFormInitialized] = useState(false)
  const generalForm = useRef()
  const [selectedFilial, setSelectedFilial] = useState(null)
  const [cashflowConfigs, setCashflowConfigs] = useState([])

  const handleCloseForm = useCallback(() => {
    toast("Changes discarded!", { autoClose: 1000 })
    handleOpened(null)
  }, [handleOpened])

  async function handleGeneralFormSubmit(data) {
    console.log("DADOS RECEBIDOS DO FORMULÁRIO:", data)

    setLoading(true)

    const payload = {
      cashflow_name: data.cashflow_name,
      cashflow_code: data.cashflow_code,
      status: "Active",
      date_type: selectedDateType,
      description: data.description || "",
      operating_revenues_accounts: data.operating_revenues_accounts || [],
      operating_expenses_accounts: data.operating_expenses_accounts || [],
      investing_revenues_accounts: data.investing_revenues_accounts || [],
      capex_accounts: data.capex_accounts || [],
      financing_inflows_accounts: data.financing_inflows_accounts || [],
      financing_outflows_accounts: data.financing_outflows_accounts || [],
    }

    if (auth?.filial?.id === 1) {
      const chosenFilialId = selectedFilial?.id || data?.filial?.id
      if (!chosenFilialId) {
        setLoading(false)
        toast("Selecione a Filial para esta configuração", {
          autoClose: 2000,
          type: "error",
          transition: Zoom,
        })
        return
      }
      payload.filial_id = Number(chosenFilialId)
    }

    console.log("Sending payload to API:", payload)

    if (id === "new") {
      api
        .post("/cashflowconfigs/create-config", payload)
        .then(({ data: response }) => {
          console.log("API Response:", response)
          setPageData({ ...pageData, ...response })
          setSuccessfullyUpdated(true)
          toast("Cashflow Configuration created successfully!", {
            autoClose: 2000,
            type: "success",
            transition: Zoom,
          })
          setLoading(false)
          handleOpened(null)
        })
        .catch((err) => {
          console.error("Error creating Cashflow config:", err)
          toast(err?.response?.data?.error || "Error creating Cashflow configuration!", {
            autoClose: 3000,
            type: "error",
            transition: Zoom,
          })
          setLoading(false)
        })
    } else {
      
      api
        .put(`/cashflowconfigs/update-config/${id}`, payload)
        .then(({ data: response }) => {
          console.log("API Response:", response)
          setPageData({ ...pageData, ...response })
          setSuccessfullyUpdated(true)
          toast("Cashflow Configuration updated successfully!", {
            autoClose: 2000,
            type: "success",
            transition: Zoom,
          })
          setLoading(false)
          handleOpened(null)
        })
        .catch((err) => {
          console.error("Error updating Cashflow config:", err)
          toast(err?.response?.data?.error || "Error updating Cashflow configuration!", {
            autoClose: 3000,
            type: "error",
            transition: Zoom,
          })
          setLoading(false)
        })
    }
  }

  async function loadCashflowConfig() {
    if (id !== "new") {
      setLoading(true)
      try {
        const { data } = await api.get(`/cashflowconfig/${id}`)
        const loadedData = {
          loaded: true,
          cashflow_name: data.cashflow_name || "",
          cashflow_code: data.cashflow_code || "",
          description: data.description || "",
          date_type: data.date_type || "due_date",
          operating_revenues_accounts: data.operating_revenues_accounts || [],
          operating_expenses_accounts: data.operating_expenses_accounts || [],
          investing_revenues_accounts: data.investing_revenues_accounts || [],
          capex_accounts: data.capex_accounts || [],
          financing_inflows_accounts: data.financing_inflows_accounts || [],
          financing_outflows_accounts: data.financing_outflows_accounts || [],
          filial_id: data.filial_id ?? null,
          filial: data.filial || null,
        }
        setPageData(loadedData)
        setSelectedDateType(loadedData.date_type)
      } catch (err) {
        console.error("Error loading Cashflow config:", err)
        toast.error(err?.response?.data?.error || "Error loading Cashflow configuration!")
      } finally {
        setLoading(false)
      }
    } else {
      setPageData((p) => ({ ...p, loaded: true }))
    }
  }

  useEffect(() => {
    setFormInitialized(false)
    loadCashflowConfig()
  }, [id])

  useEffect(() => {
    if (pageData.loaded && generalForm.current && id !== "new" && !formInitialized) {
      generalForm.current.setData({
        cashflow_name: pageData.cashflow_name,
        cashflow_code: pageData.cashflow_code,
        description: pageData.description,
        date_type: pageData.date_type,
        operating_revenues_accounts: pageData.operating_revenues_accounts,
        operating_expenses_accounts: pageData.operating_expenses_accounts,
        investing_revenues_accounts: pageData.investing_revenues_accounts,
        capex_accounts: pageData.capex_accounts,
        financing_inflows_accounts: pageData.financing_inflows_accounts,
        financing_outflows_accounts: pageData.financing_outflows_accounts,
      })
      setFormInitialized(true)
    }
  }, [pageData.loaded, id, formInitialized])

  const inputContextValue = useMemo(
    () => ({ id, generalForm, handleCloseForm, setSuccessfullyUpdated }),
    [id, handleCloseForm, setSuccessfullyUpdated]
  )

  return (
    <Preview formType="full">
      {pageData.loaded ? (
        <InputContext.Provider value={inputContextValue}>
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
              <RegisterFormMenu setActiveMenu={setActiveMenu} activeMenu={activeMenu} name="general">
                <Building size={16} /> General
              </RegisterFormMenu>
              <RegisterFormMenu setActiveMenu={setActiveMenu} activeMenu={activeMenu} name="operating">
                <DollarSign size={16} /> Operating
              </RegisterFormMenu>
              <RegisterFormMenu setActiveMenu={setActiveMenu} activeMenu={activeMenu} name="investing">
                <TrendingUp size={16} /> Investing
              </RegisterFormMenu>
              <RegisterFormMenu setActiveMenu={setActiveMenu} activeMenu={activeMenu} name="financing">
                <TrendingDown size={16} /> Financing
              </RegisterFormMenu>
            </div>

            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form ref={generalForm} onSubmit={handleGeneralFormSubmit} className="w-full">
                  <FormHeader access={access} title={pageData.cashflow_name} InputContext={InputContext} />

                  <InputLineGroup title="GENERAL" activeMenu={activeMenu === "general"}>
                    {id === "new" && (
                      <FindGeneric
                        route="filials"
                        title="Filial"
                        scope="filial"
                        required
                        InputContext={InputContext}
                        defaultValue={
                          selectedFilial
                            ? selectedFilial
                            : (auth?.filial?.id !== 1
                              ? { id: auth.filial.id, name: auth.filial.name }
                              : { id: pageData.filial_id, name: pageData.filial?.name })
                        }
                        setReturnFindGeneric={(row) => setSelectedFilial(row)}
                        fields={[{ title: "Name", name: "name" }]}
                      />
                    )}

                    <InputLine title="Basic Information">
                      <Input type="text" name="cashflow_name" required title="Cashflow Name" grow InputContext={InputContext} />
                      <Input type="text" name="cashflow_code" required title="Cashflow Code" grow InputContext={InputContext} />
                    </InputLine>

                    <InputLine title="Date Type">
                      <div className="w-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="date_type"
                          required
                          value={selectedDateType}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                          onChange={(e) => {
                            setSelectedDateType(e.target.value)
                          }}
                        >
                          {dateTypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </InputLine>

                    <InputLine title="Description">
                      <Textarea name="description" title="Description" rows={4} grow InputContext={InputContext} placeholder="Optional notes..." />
                    </InputLine>
                  </InputLineGroup>

                  <InputLineGroup title="OPERATING ACTIVITIES" activeMenu={activeMenu === "operating"}>
                    <AccountsMultiSelect
                      title="Operating Revenues"
                      name="operating_revenues_accounts"
                      InputContext={InputContext}
                      useApi
                      filterPrefix="01"
                      defaultValue={pageData.operating_revenues_accounts}
                    />
                    <AccountsMultiSelect
                      title="Operating Expenses"
                      name="operating_expenses_accounts"
                      InputContext={InputContext}
                      useApi
                      filterPrefix="02"
                      defaultValue={pageData.operating_expenses_accounts}
                    />
                  </InputLineGroup>

                  <InputLineGroup title="INVESTING ACTIVITIES" activeMenu={activeMenu === "investing"}>
                    <AccountsMultiSelect
                      title="Investing Revenues"
                      name="investing_revenues_accounts"
                      InputContext={InputContext}
                      useApi
                      filterPrefix="01"
                      defaultValue={pageData.investing_revenues_accounts}
                    />
                    <AccountsMultiSelect
                      title="Capex"
                      name="capex_accounts"
                      InputContext={InputContext}
                      useApi
                      filterPrefix="02"
                      defaultValue={pageData.capex_accounts}
                    />
                  </InputLineGroup>

                  <InputLineGroup title="FINANCING ACTIVITIES" activeMenu={activeMenu === "financing"}>
                    <AccountsMultiSelect
                      title="Financing Inflows"
                      name="financing_inflows_accounts"
                      InputContext={InputContext}
                      useApi
                      filterPrefix="01"
                      defaultValue={pageData.financing_inflows_accounts}
                    />
                    <AccountsMultiSelect
                      title="Financing Outflows"
                      name="financing_outflows_accounts"
                      InputContext={InputContext}
                      useApi
                      filterPrefix="02"
                      defaultValue={pageData.financing_outflows_accounts}
                    />
                  </InputLineGroup>
                </Form>
              </div>
            </div>
          </div>
        </InputContext.Provider>
      ) : (
        <FormLoading />
      )}
    </Preview>
  )
}
